import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
 FolderOpen,
 FileText,
 Search,
 Upload,
 Download,
 Trash2,
 Shield,
 X,
 Grid2x2,
 List,
 Book,
 Users,
 Megaphone,
 ChevronRight,
 ChevronDown,
 Eye,
 Tag,
 Clock,
 File,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import PermissionsModal from './components/PermissionsModal';
import UploadModal from './components/UploadModal';

// ---- Types ----
interface DocCategory {
 id: string;
 name: string;
 icon: string;
 count: number;
 children: { id: string; name: string; count: number }[];
}

interface Document {
 id: string;
 name: string;
 category: string;
 type: string;
 size: string;
 version: string;
 uploadDate: string;
 lastModified: string;
 uploadedBy: string;
 permissions: string[];
 downloads: number;
 description: string;
 tags: string[];
}

// ---- Mock Data ----
const categories: DocCategory[] = [
 { id: 'all', name: 'All Documents', icon: 'FolderOpen', count: 156, children: [] },
 {
 id: 'employee-handbook', name: 'Employee Handbook', icon: 'Book', count: 24,
 children: [
 { id: 'code-of-conduct', name: 'Code of Conduct', count: 8 },
 { id: 'employee-benefits', name: 'Employee Benefits', count: 12 },
 { id: 'workplace-policies', name: 'Workplace Policies', count: 4 },
 ],
 },
 {
 id: 'hr-policies', name: 'HR Policies', icon: 'Users', count: 32,
 children: [
 { id: 'recruitment', name: 'Recruitment', count: 8 },
 { id: 'performance-management', name: 'Performance Management', count: 6 },
 { id: 'disciplinary-procedures', name: 'Disciplinary Procedures', count: 4 },
 { id: 'leave-policies', name: 'Leave Policies', count: 14 },
 ],
 },
 {
 id: 'compliance', name: 'Compliance Documents', icon: 'Shield', count: 18,
 children: [
 { id: 'labor-law', name: 'Labor Law Compliance', count: 8 },
 { id: 'safety-regulations', name: 'Safety Regulations', count: 6 },
 { id: 'data-protection', name: 'Data Protection', count: 4 },
 ],
 },
 {
 id: 'forms-templates', name: 'Forms & Templates', icon: 'FileText', count: 45,
 children: [
 { id: 'hr-forms', name: 'HR Forms', count: 20 },
 { id: 'leave-forms', name: 'Leave Forms', count: 15 },
 { id: 'evaluation-forms', name: 'Evaluation Forms', count: 10 },
 ],
 },
 {
 id: 'announcements', name: 'Company Announcements', icon: 'Megaphone', count: 37,
 children: [
 { id: 'policy-updates', name: 'Policy Updates', count: 12 },
 { id: 'company-news', name: 'Company News', count: 18 },
 { id: 'events', name: 'Events & Activities', count: 7 },
 ],
 },
];

const documents: Document[] = [
 {
 id: '1', name: 'Employee Handbook 2024', category: 'employee-handbook', type: 'pdf',
 size: '2.4 MB', version: '3.2', uploadDate: '2024-01-15', lastModified: '2024-01-15',
 uploadedBy: 'HR Department', permissions: ['employee', 'manager', 'admin'],
 downloads: 142, description: 'Comprehensive employee handbook covering all policies and procedures for 2024',
 tags: ['handbook', 'policies', '2024'],
 },
 {
 id: '2', name: 'Code of Conduct Guidelines', category: 'employee-handbook', type: 'pdf',
 size: '1.8 MB', version: '2.1', uploadDate: '2024-02-01', lastModified: '2024-02-01',
 uploadedBy: 'Legal Department', permissions: ['employee', 'manager', 'admin'],
 downloads: 98, description: 'Updated code of conduct guidelines and ethical standards',
 tags: ['conduct', 'ethics', 'guidelines'],
 },
 {
 id: '3', name: 'Remote Work Policy', category: 'hr-policies', type: 'docx',
 size: '856 KB', version: '1.5', uploadDate: '2024-01-22', lastModified: '2024-02-08',
 uploadedBy: 'HR Manager', permissions: ['manager', 'admin'],
 downloads: 67, description: 'Comprehensive remote work policy and guidelines for hybrid work arrangements',
 tags: ['remote', 'hybrid', 'policy'],
 },
 {
 id: '4', name: 'Performance Review Template', category: 'forms-templates', type: 'xlsx',
 size: '124 KB', version: '4.0', uploadDate: '2024-02-10', lastModified: '2024-02-10',
 uploadedBy: 'HR Specialist', permissions: ['manager', 'admin'],
 downloads: 45, description: 'Annual performance review template with updated KPI metrics',
 tags: ['performance', 'review', 'template', 'KPI'],
 },
 {
 id: '5', name: 'Health & Safety Guidelines', category: 'compliance', type: 'pdf',
 size: '3.2 MB', version: '2.8', uploadDate: '2024-01-08', lastModified: '2024-01-30',
 uploadedBy: 'Safety Officer', permissions: ['employee', 'manager', 'admin'],
 downloads: 89, description: 'Complete health and safety guidelines for office and remote work environments',
 tags: ['safety', 'health', 'compliance'],
 },
 {
 id: '6', name: 'Q1 2024 Company Update', category: 'announcements', type: 'pdf',
 size: '945 KB', version: '1.0', uploadDate: '2024-02-15', lastModified: '2024-02-15',
 uploadedBy: 'Executive Team', permissions: ['employee', 'manager', 'admin'],
 downloads: 203, description: 'Quarterly company update including achievements, goals, and upcoming initiatives',
 tags: ['quarterly', 'update', 'company', 'Q1'],
 },
];

// ---- Helpers ----
const getCategoryIcon = (iconName: string) => {
 switch (iconName) {
 case 'FolderOpen': return <FolderOpen className="w-4 h-4" />;
 case 'Book': return <Book className="w-4 h-4" />;
 case 'Users': return <Users className="w-4 h-4" />;
 case 'Shield': return <Shield className="w-4 h-4" />;
 case 'FileText': return <FileText className="w-4 h-4" />;
 case 'Megaphone': return <Megaphone className="w-4 h-4" />;
 default: return <FolderOpen className="w-4 h-4" />;
 }
};

const getFileTypeColor = (type: string): string => {
 switch (type) {
 case 'pdf': return 'bg-red-100 text-red-700';
 case 'docx': return 'bg-blue-100 text-blue-700';
 case 'xlsx': return 'bg-green-100 text-green-700';
 default: return 'bg-gray-100 text-gray-700';
 }
};

// ---- Main Component ----
export default function CompanyFilesPolicies() {
 const { t } = useTranslation();
 const [selectedCategory, setSelectedCategory] = useState('all');
 const [searchTerm, setSearchTerm] = useState('');
 const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
 const [sortBy, setSortBy] = useState('date-desc');
 const [filterBy, setFilterBy] = useState('all');
 const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
 const [showUploadModal, setShowUploadModal] = useState(false);
 const [showPermissionsModal, setShowPermissionsModal] = useState(false);
 const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);

 const toggleCategory = (id: string) => {
 setExpandedCategories((prev) => {
 const next = new Set(prev);
 if (next.has(id)) next.delete(id); else next.add(id);
 return next;
 });
 };

 const handleManagePermissions = (docIds: string[]) => {
 setSelectedDocuments(docIds);
 setShowPermissionsModal(true);
 };

 // Filter & sort documents
 const filteredDocuments = documents
 .filter((doc) => {
 const matchesCategory = selectedCategory === 'all' || doc.category === selectedCategory;
 const matchesSearch =
 doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
 doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
 doc.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
 const matchesPermissions = filterBy === 'all' || doc.permissions.includes(filterBy);
 return matchesCategory && matchesSearch && matchesPermissions;
 })
 .sort((a, b) => {
 switch (sortBy) {
 case 'date-desc': return new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime();
 case 'date-asc': return new Date(a.uploadDate).getTime() - new Date(b.uploadDate).getTime();
 case 'name-asc': return a.name.localeCompare(b.name);
 case 'name-desc': return b.name.localeCompare(a.name);
 case 'downloads-desc': return b.downloads - a.downloads;
 default: return 0;
 }
 });

 return (
 <div className="p-6 max-w-7xl mx-auto">
 <ComingSoonBanner
 icon={<FolderOpen className="w-6 h-6" />}
 title="Company Files & HR Policies"
 description="Document management is currently in development. This is a preview of the upcoming feature."
 gradientFrom="from-amber-500"
 gradientTo="to-yellow-500"
 />

 {/* Header */}
 <div className="mb-8">
 <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
 <div className="flex items-center mb-4">
 <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
 <FileText className="w-6 h-6 text-white" />
 </div>
 <div>
 <h1 className="text-3xl font-bold text-gray-900">{t('Company Files & HR Policies')}</h1>
 <p className="text-gray-600">{t('Centralized document management with version control and access permissions')}</p>
 </div>
 </div>
 <div className="flex items-center space-x-2 text-sm text-gray-500">
 <File className="w-4 h-4" />
 <span>{filteredDocuments.length} {t('documents')}</span>
 </div>
 </div>
 </div>

 {/* Controls Bar */}
 <div className="bg-card border border-border rounded-xl shadow-sm p-6 mb-6">
 <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
 {/* Left Controls */}
 <div className="flex items-center space-x-4">
 <div className="relative">
 <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700" />
 <input
 type="text"
 placeholder={t('Search documents, descriptions, tags...')}
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10 pr-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 w-80"
 />
 </div>
 <select
 value={filterBy}
 onChange={(e) => setFilterBy(e.target.value)}
 className="border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
 >
 <option value="all">{t('All Access Levels')}</option>
 <option value="employee">{t('Employee Access')}</option>
 <option value="manager">{t('Manager Access')}</option>
 <option value="admin">{t('Admin Only')}</option>
 </select>
 <select
 value={sortBy}
 onChange={(e) => setSortBy(e.target.value)}
 className="border border-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
 >
 <option value="date-desc">{t('Newest First')}</option>
 <option value="date-asc">{t('Oldest First')}</option>
 <option value="name-asc">{t('Name A-Z')}</option>
 <option value="name-desc">{t('Name Z-A')}</option>
 <option value="downloads-desc">{t('Most Downloaded')}</option>
 </select>
 </div>

 {/* Right Controls */}
 <div className="flex items-center space-x-3">
 <div className="flex items-center border border-border rounded-lg p-1">
 <button
 onClick={() => setViewMode('grid')}
 className={`p-2 rounded transition-colors ${
 viewMode === 'grid' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'
 }`}
 >
 <Grid2x2 className="w-4 h-4" />
 </button>
 <button
 onClick={() => setViewMode('list')}
 className={`p-2 rounded transition-colors ${
 viewMode === 'list' ? 'bg-amber-500 text-white' : 'text-gray-600 hover:bg-gray-100'
 }`}
 >
 <List className="w-4 h-4" />
 </button>
 </div>
 <button
 className="flex items-center space-x-2 px-4 py-2 bg-amber-500 text-white rounded-lg"
 onClick={() => setShowUploadModal(true)}
 >
 <Upload className="w-4 h-4" />
 <span>{t('Upload Files')}</span>
 </button>
 </div>
 </div>
 </div>

 {/* Main Content */}
 <div className="flex gap-6">
 {/* Category Sidebar */}
 <div className="w-80 flex-shrink-0">
 <div className="bg-card rounded-lg shadow-sm border border-border p-4">
 <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('Categories')}</h3>
 <div className="space-y-1">
 {categories.map((cat) => (
 <div key={cat.id}>
 <button
 onClick={() => {
 setSelectedCategory(cat.id);
 if (cat.children.length > 0) toggleCategory(cat.id);
 }}
 className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
 selectedCategory === cat.id ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
 }`}
 >
 <div className="flex items-center space-x-2">
 {getCategoryIcon(cat.icon)}
 <span>{t(cat.name)}</span>
 </div>
 <div className="flex items-center space-x-1">
 <span className="text-xs text-gray-700">{cat.count}</span>
 {cat.children.length > 0 && (
 expandedCategories.has(cat.id) ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />
 )}
 </div>
 </button>
 {cat.children.length > 0 && expandedCategories.has(cat.id) && (
 <div className="ml-6 mt-1 space-y-1">
 {cat.children.map((child) => (
 <button
 key={child.id}
 onClick={() => setSelectedCategory(child.id)}
 className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs transition-colors ${
 selectedCategory === child.id ? 'bg-amber-50 text-amber-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
 }`}
 >
 <span>{t(child.name)}</span>
 <span className="text-gray-700">{child.count}</span>
 </button>
 ))}
 </div>
 )}
 </div>
 ))}
 </div>
 </div>
 </div>

 {/* Document List/Grid */}
 <div className="flex-1">
 <div className="bg-card rounded-lg shadow-sm border border-border min-h-[600px]">
 {filteredDocuments.length === 0 ? (
 <div className="flex flex-col items-center justify-center py-20">
 <FolderOpen className="w-16 h-16 text-gray-600 mb-4" />
 <h3 className="text-lg font-medium text-gray-900 mb-2">{t('No documents found')}</h3>
 <p className="text-gray-500">{t('Try adjusting your search or filter criteria')}</p>
 </div>
 ) : viewMode === 'grid' ? (
 <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
 {filteredDocuments.map((doc) => (
 <div key={doc.id} className="border border-border rounded-lg p-4 hover:border-border hover:shadow-sm transition-all">
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-center space-x-2">
 <FileText className="w-8 h-8 text-gray-700" />
 <div>
 <h4 className="text-sm font-medium text-gray-900 line-clamp-1">{doc.name}</h4>
 <span className={`inline-flex px-1.5 py-0.5 text-xs rounded ${getFileTypeColor(doc.type)}`}>
 {doc.type.toUpperCase()}
 </span>
 </div>
 </div>
 </div>
 <p className="text-xs text-gray-500 line-clamp-2 mb-3">{doc.description}</p>
 <div className="flex items-center justify-between text-xs text-gray-700">
 <div className="flex items-center space-x-1">
 <Clock className="w-3 h-3" />
 <span>{doc.uploadDate}</span>
 </div>
 <div className="flex items-center space-x-1">
 <Download className="w-3 h-3" />
 <span>{doc.downloads}</span>
 </div>
 </div>
 <div className="flex items-center gap-1 mt-2 flex-wrap">
 {doc.tags.map((tag) => (
 <span key={tag} className="inline-flex items-center px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
 <Tag className="w-2.5 h-2.5 mr-0.5" />{tag}
 </span>
 ))}
 </div>
 <div className="mt-3 pt-3 border-t border-border flex items-center justify-end space-x-2">
 <button className="p-1.5 text-gray-600">
 <Eye className="w-4 h-4" />
 </button>
 <button className="p-1.5 text-gray-600">
 <Download className="w-4 h-4" />
 </button>
 <button className="p-1.5 text-gray-600" onClick={() => handleManagePermissions([doc.id])}>
 <Shield className="w-4 h-4" />
 </button>
 <button className="p-1.5 text-gray-600">
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="min-w-full divide-y divide-gray-200">
 <thead className="bg-gray-50">
 <tr>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Document')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Type')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Size')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Version')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Uploaded')}</th>
 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('Downloads')}</th>
 <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('Actions')}</th>
 </tr>
 </thead>
 <tbody className="bg-white divide-y divide-gray-200">
 {filteredDocuments.map((doc) => (
 <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center space-x-3">
 <FileText className="w-5 h-5 text-gray-700" />
 <div>
 <div className="text-sm font-medium text-gray-900">{doc.name}</div>
 <div className="text-xs text-gray-500">{doc.uploadedBy}</div>
 </div>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className={`inline-flex px-2 py-0.5 text-xs rounded ${getFileTypeColor(doc.type)}`}>
 {doc.type.toUpperCase()}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.size}</td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">v{doc.version}</td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.uploadDate}</td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.downloads}</td>
 <td className="px-6 py-4 whitespace-nowrap text-right">
 <div className="flex items-center justify-end space-x-2">
 <button className="text-gray-600">
 <Eye className="w-4 h-4" />
 </button>
 <button className="text-gray-600">
 <Download className="w-4 h-4" />
 </button>
 <button className="text-gray-600" onClick={() => handleManagePermissions([doc.id])}>
 <Shield className="w-4 h-4" />
 </button>
 <button className="text-gray-600">
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>
 </div>
 </div>

 {/* Modals */}
 {showUploadModal && (
 <UploadModal
 selectedCategory={selectedCategory}
 categories={categories}
 onClose={() => setShowUploadModal(false)}
 />
 )}
 {showPermissionsModal && (
 <PermissionsModal
 selectedDocuments={selectedDocuments}
 documents={documents}
 onClose={() => setShowPermissionsModal(false)}
 />
 )}
 </div>
 );
}
